import React from 'react';
import { isUndefined } from 'lodash-es';

import { CONFIGURATOR_PREVIEW_LOAD_BUFFER } from '../constants';
import {
  ConfiguratorThemeColorsOption,
  ConfiguratorThemeDesignOption,
  DesignVariantRequestParameters,
  Maybe,
} from '../types';

export function useIframe(
  isMobile: boolean,
  themeDesignOption: Maybe<ConfiguratorThemeDesignOption>,
  themeColorsOption: Maybe<ConfiguratorThemeColorsOption>,
) {
  const [iframeLoading, setIframeLoading] = React.useState<boolean>(false);
  const [iframeUrlParams, setIframeUrlParams] = React.useState<Maybe<string>>();
  const [debuggingTimestamp, setDebuggingTimestamp] = React.useState<number>(0);

  React.useEffect(() => {
    if (!isUndefined(themeDesignOption) && !isUndefined(themeColorsOption)) {
      const parameters: DesignVariantRequestParameters = {
        wsid: themeDesignOption.designVariant,
        wsp: themeDesignOption.designPage,
        wsc: themeColorsOption.colorsId,
      };

      const urlParams = new URLSearchParams(parameters);
      const nextIframeUrlParams = urlParams.toString();

      if (iframeUrlParams !== nextIframeUrlParams) {
        console.log('SparkConfigurator[Preview]: Changed');

        setDebuggingTimestamp(Date.now());

        setIframeLoading(true);

        setIframeUrlParams(nextIframeUrlParams);
      }
    }
  }, [themeDesignOption, themeColorsOption, iframeUrlParams]);

  const handleIframeLoad = React.useCallback<EventListener>(() => {
    const loadTime = Date.now() - debuggingTimestamp;
    // The event triggers when the index.html is loaded from server, but after that the site still has its loading icon.
    // For now, we could use an additional buffer of 2 seconds to ensure the site is fully loaded, or if the event time is longer,
    // double it (in case the connection is slow and affects downloading all the assets after loading icon).
    // TODO: maybe tweak the timing more
    const timeBuffer = loadTime > CONFIGURATOR_PREVIEW_LOAD_BUFFER ? loadTime : CONFIGURATOR_PREVIEW_LOAD_BUFFER;

    setTimeout(() => {
      console.log('SparkConfigurator[Preview]: Loaded', loadTime / 1000, 'seconds');

      setIframeLoading(false);
    }, timeBuffer);
  }, [debuggingTimestamp]);

  React.useEffect(() => {
    const deviceClassName = isMobile ? 'mobile' : 'desktop';
    const iframeWrapperSelector = `.configurator-${deviceClassName}-preview-wrapper`;
    const iframeElements = Array.from(document.querySelectorAll(`${iframeWrapperSelector} iframe`));

    if (iframeElements.length !== 1) {
      return;
    }

    const iframe = iframeElements[0];

    iframe.addEventListener('load', handleIframeLoad);

    return () => {
      iframe.removeEventListener('load', handleIframeLoad);
    };
  }, [isMobile, handleIframeLoad]);

  return React.useMemo(() => ({ iframeLoading, iframeUrlParams }), [iframeLoading, iframeUrlParams]);
}
